import React, { useState, useEffect } from "react";
import { 
	Activity, 
	Clock, 
	MessageSquare, 
	CheckCircle, 
	XCircle, 
	AlertCircle,
	Mail,
	TrendingUp,
	RefreshCw,
	Filter,
	Calendar,
	BarChart3,
	Users,
	Zap
} from "lucide-react";

const WebhookAnalyticsReal = () => {
	const [webhookData, setWebhookData] = useState({
		totalEvents: 0,
		successRate: 0,
		avgResponseTime: 0,
		errorRate: 0,
		messageDeliveryStatus: {
			sent: { count: 0, percentage: 0 },
			delivered: { count: 0, percentage: 0 },
			read: { count: 0, percentage: 0 },
			failed: { count: 0, percentage: 0 },
			pending: { count: 0, percentage: 0 }
		},
		hourlyActivity: Array(24).fill(0),
		recentEvents: [],
		conversationMetrics: {
			activeConversations: 0,
			avgMessagesPerConversation: 0,
			avgSessionLength: 0
		},
		userEngagementMetrics: {
			activeUsers: 0,
			avgEngagementScore: 0,
			totalReads: 0
		},
		performance: {
			avgResponseTime: 0,
			peakHour: '00:00',
			totalMessages: 0
		}
	});
	
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [timeRange, setTimeRange] = useState('24h');
	const [lastUpdate, setLastUpdate] = useState(new Date());

	const SERVER_URL = 'https://heyakua-whatsapp-bot-655772124305.us-central1.run.app';

	const loadWebhookData = async () => {
		setLoading(true);
		setError(null);
		
		try {
			const response = await fetch(`${SERVER_URL}/webhook/analytics?range=${timeRange}`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
				},
			});

			if (!response.ok) {
				throw new Error(`Server error: ${response.status}`);
			}

			const result = await response.json();
			
			if (result.success) {
				setWebhookData(result.data);
				setLastUpdate(new Date());
			} else {
				throw new Error(result.error || 'Failed to fetch webhook analytics');
			}
			
		} catch (error) {
			console.error('Error loading webhook analytics:', error);
			setError(error.message);
			
			// Fallback to empty data
			setWebhookData({
				totalEvents: 0,
				successRate: 0,
				avgResponseTime: 0,
				errorRate: 0,
				messageDeliveryStatus: {
					sent: { count: 0, percentage: 0 },
					delivered: { count: 0, percentage: 0 },
					read: { count: 0, percentage: 0 },
					failed: { count: 0, percentage: 0 },
					pending: { count: 0, percentage: 0 }
				},
				hourlyActivity: Array(24).fill(0),
				recentEvents: [],
				conversationMetrics: { activeConversations: 0, avgMessagesPerConversation: 0, avgSessionLength: 0 },
				userEngagementMetrics: { activeUsers: 0, avgEngagementScore: 0, totalReads: 0 },
				performance: { avgResponseTime: 0, peakHour: '00:00', totalMessages: 0 }
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadWebhookData();
	}, [timeRange]);

	const StatCard = ({ title, value, icon: Icon, color, subtitle, trend }) => (
		<div className="bg-white rounded-lg shadow-md p-6">
			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm font-medium text-gray-600">{title}</p>
					<p className={`text-2xl font-bold ${color}`}>{value}</p>
					<p className="text-xs text-gray-500">{subtitle}</p>
					{trend && (
						<div className={`flex items-center mt-1 text-xs ${
							trend.startsWith('+') ? 'text-green-600' : 'text-red-600'
						}`}>
							<TrendingUp className="h-3 w-3 mr-1" />
							{trend}
						</div>
					)}
				</div>
				<div className={`p-3 rounded-full bg-opacity-20 ${color.replace('text-', 'bg-')}`}>
					<Icon className={`h-6 w-6 ${color}`} />
				</div>
			</div>
		</div>
	);

	const getStatusColor = (status) => {
		switch (status) {
			case 'sent': return 'bg-blue-500';
			case 'delivered': return 'bg-green-500';
			case 'read': return 'bg-purple-500';
			case 'failed': return 'bg-red-500';
			case 'pending': return 'bg-yellow-500';
			default: return 'bg-gray-500';
		}
	};

	const getStatusIcon = (status) => {
		switch (status) {
			case 'sent': return Mail;
			case 'delivered': return CheckCircle;
			case 'read': return CheckCircle;
			case 'failed': return XCircle;
			case 'pending': return Clock;
			default: return AlertCircle;
		}
	};

	const maxHourlyActivity = Math.max(...webhookData.hourlyActivity);
	const peakHour = webhookData.hourlyActivity.indexOf(maxHourlyActivity);

	return (
		<div className="p-6 bg-gray-50 min-h-screen">
			<div className="max-w-7xl mx-auto">
				{/* Header */}
				<div className="flex justify-between items-center mb-8">
					<div>
						<h2 className="text-3xl font-bold text-gray-900">Real-Time Webhook Analytics</h2>
						<p className="text-gray-600 mt-2">Advanced WhatsApp API webhook monitoring & performance insights</p>
					</div>
					
					<div className="flex space-x-4">
						<select
							value={timeRange}
							onChange={(e) => setTimeRange(e.target.value)}
							className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
						>
							<option value="1h">Last Hour</option>
							<option value="24h">Last 24 Hours</option>
							<option value="7d">Last 7 Days</option>
						</select>
						
						<button
							onClick={loadWebhookData}
							disabled={loading}
							className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 ${
								loading ? 'opacity-50 cursor-not-allowed' : ''
							}`}
						>
							<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
							<span>Refresh</span>
						</button>
					</div>
				</div>

				{error && (
					<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
						<div className="flex items-center">
							<XCircle className="h-5 w-5 text-red-500 mr-2" />
							<span className="text-red-700">Error loading webhook data: {error}</span>
						</div>
					</div>
				)}

				{/* Main Stats */}
				<div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
					<StatCard
						title="Total Events"
						value={webhookData.totalEvents}
						icon={Activity}
						color="text-blue-600"
						subtitle="Webhook events processed"
					/>
					<StatCard
						title="Success Rate"
						value={`${webhookData.successRate}%`}
						icon={CheckCircle}
						color="text-green-600"
						subtitle="Events successfully processed"
					/>
					<StatCard
						title="Avg Response Time"
						value={`${Math.round(webhookData.avgResponseTime)}ms`}
						icon={Clock}
						color="text-purple-600"
						subtitle="API response time"
					/>
					<StatCard
						title="Error Rate"
						value={`${webhookData.errorRate}%`}
						icon={XCircle}
						color="text-red-600"
						subtitle="Failed webhook events"
					/>
				</div>

				{/* Advanced Metrics */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
					<StatCard
						title="Active Conversations"
						value={webhookData.conversationMetrics.activeConversations}
						icon={MessageSquare}
						color="text-indigo-600"
						subtitle="Ongoing conversations"
					/>
					<StatCard
						title="Active Users"
						value={webhookData.userEngagementMetrics.activeUsers}
						icon={Users}
						color="text-cyan-600"
						subtitle="Engaged users"
					/>
					<StatCard
						title="Avg Engagement Score"
						value={webhookData.userEngagementMetrics.avgEngagementScore.toFixed(1)}
						icon={Zap}
						color="text-yellow-600"
						subtitle="User engagement level"
					/>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
					{/* Message Delivery Status */}
					<div className="bg-white rounded-lg shadow-md p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">Message Delivery Status</h3>
						<div className="space-y-4">
							{Object.entries(webhookData.messageDeliveryStatus).map(([status, data]) => {
								const Icon = getStatusIcon(status);
								return (
									<div key={status} className="flex items-center justify-between">
										<div className="flex items-center space-x-3">
											<div className={`w-3 h-3 rounded-full ${getStatusColor(status)}`}></div>
											<span className="capitalize font-medium">{status}</span>
										</div>
										<div className="text-right">
											<span className="font-bold text-lg">{data.count}</span>
											<span className="text-sm text-gray-500 ml-2">{data.percentage}%</span>
										</div>
									</div>
								);
							})}
						</div>
					</div>

					{/* Hourly Activity Chart */}
					<div className="bg-white rounded-lg shadow-md p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">Hourly Webhook Activity</h3>
						<div className="space-y-2">
							{webhookData.hourlyActivity.map((count, hour) => (
								<div key={hour} className="flex items-center space-x-3">
									<span className="text-sm font-medium text-gray-600 w-12">
										{hour.toString().padStart(2, '0')}:00
									</span>
									<div className="flex-1 bg-gray-200 rounded-full h-2">
										<div
											className={`h-2 rounded-full transition-all duration-300 ${
												count === maxHourlyActivity && count > 0
													? 'bg-blue-600'
													: 'bg-blue-400'
											}`}
											style={{
												width: maxHourlyActivity > 0 ? `${(count / maxHourlyActivity) * 100}%` : '0%'
											}}
										></div>
									</div>
									<span className="text-sm text-gray-500 w-8">{count}</span>
								</div>
							))}
						</div>
						{maxHourlyActivity > 0 && (
							<p className="text-sm text-gray-600 mt-4">
								Peak activity: {peakHour.toString().padStart(2, '0')}:00 ({maxHourlyActivity} events)
							</p>
						)}
					</div>
				</div>

				{/* Recent Events */}
				<div className="mt-8 bg-white rounded-lg shadow-md">
					<div className="p-6 border-b border-gray-200">
						<h3 className="text-lg font-semibold text-gray-900">Recent Webhook Events</h3>
					</div>
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Event
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Status
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										User ID
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Response Time
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Time
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Details
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{webhookData.recentEvents.length === 0 ? (
									<tr>
										<td colSpan={6} className="px-6 py-4 text-center text-gray-500">
											No recent webhook events found
										</td>
									</tr>
								) : (
									webhookData.recentEvents.map((event, index) => (
										<tr key={index} className="hover:bg-gray-50">
											<td className="px-6 py-4 whitespace-nowrap">
												<div className="flex items-center">
													<span className={`inline-block w-2 h-2 rounded-full mr-3 ${getStatusColor(event.status)}`}></span>
													<span className="text-sm font-medium text-gray-900">{event.type}</span>
												</div>
											</td>
											<td className="px-6 py-4 whitespace-nowrap">
												<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
													event.status === 'success' || event.status === 'delivered' || event.status === 'read'
														? 'bg-green-100 text-green-800'
														: event.status === 'failed'
														? 'bg-red-100 text-red-800'
														: 'bg-yellow-100 text-yellow-800'
												}`}>
													{event.status}
												</span>
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
												{event.user_id}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
												{event.response_time}
											</td>
											<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
												{event.time}
											</td>
											<td className="px-6 py-4 text-sm text-gray-500">
												{event.details}
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>

				{/* Footer */}
				<div className="mt-8 text-center text-sm text-gray-500">
					Last updated: {lastUpdate.toLocaleString()}
					{loading && <span className="ml-2">• Refreshing...</span>}
				</div>
			</div>
		</div>
	);
};

export default WebhookAnalyticsReal;
